/**
 * 🚀 [Phase v13.47.1 — 2026-07-18] Modal « Nouveau message » (conversation directe).
 *
 * Diffère de `ModalNouveauGroupe` :
 *   - 1 seul destinataire sélectionnable (radio-like, pas de checkbox multi)
 *   - Pas de champ « nom du groupe »
 *   - Appelle `POST /api/messagerie/conversations` (endpoint direct 1-à-1
 *     déjà existant, cf. backend/routers/messagerie.py L88-98) — aucun nouveau
 *     backend introduit.
 *
 * FR-CA. A11y : `aria-label` sur le bouton parent + focus auto sur la recherche.
 */
import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EmployeeAvatar } from '@/components/EmployeeAvatar';
import { X, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function ModalNouveauMessage({ employes, myId, myNom, onCreated, onClose }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const filtered = employes.filter(e =>
    `${e.prenom} ${e.nom}`.toLowerCase().includes(search.toLowerCase())
  );

  const create = async () => {
    if (!selected) {
      toast.error('Sélectionnez un destinataire');
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${API_URL}/api/messagerie/conversations`, {
        user1_id: myId,
        user1_nom: myNom,
        user2_id: selected.id,
        user2_nom: `${selected.prenom} ${selected.nom}`.trim(),
      });
      if (r.status === 200) {
        toast.success(`Conversation avec ${selected.prenom} ${selected.nom} ouverte`);
        onCreated(r.data.conversation);
      } else {
        toast.error('Erreur lors de la création de la conversation');
      }
    } catch (err) {
      toast.error('Erreur lors de la création de la conversation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4"
      data-testid="modal-nouveau-message"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Nouveau message</h3>
          <button onClick={onClose} aria-label="Fermer la fenêtre">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label className="text-sm">Destinataire</Label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
              <Input
                data-testid="new-message-search-input"
                placeholder="Rechercher un employé…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto mt-2">
              {filtered.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">
                  Aucun employé trouvé.
                </p>
              )}
              {filtered.map(emp => {
                const isSelected = selected?.id === emp.id;
                return (
                  <button
                    type="button"
                    key={emp.id}
                    onClick={() => setSelected(emp)}
                    data-testid={`new-message-recipient-${emp.id}`}
                    aria-pressed={isSelected}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left ${
                      isSelected
                        ? 'bg-blue-50 border border-blue-300 ring-2 ring-blue-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <EmployeeAvatar
                      photoUrl={emp.photo_url}
                      prenom={emp.prenom}
                      nom={emp.nom}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{emp.prenom} {emp.nom}</p>
                      <p className="text-xs text-gray-400">{emp.role}</p>
                    </div>
                    {isSelected && (
                      <span className="text-blue-600 text-xs font-semibold">
                        Sélectionné
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button
              data-testid="btn-create-direct-conv"
              onClick={create}
              disabled={creating || !selected}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {creating ? 'Ouverture…' : 'Ouvrir la conversation'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
